/// <amd-module name="Release/Landing/SignalR/LandingSignalRHub" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssFeatureService } from "VSS/Platform/Feature";

import { SignalRHub, IHubHostInformation, IHubListener } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";

import { traceHubException, FeatureArea } from "Release/Helpers/Telemetry";
import { ReleaseContractsStatusConverter } from "Release/Helpers/ReleaseContractsStatusConverter";
import { EnvironmentStatus, DeploymentStatus, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { HubNames, IReleaseDetailHubServer, IReleaseDetailHubClient, getReleaseProxyToRegister } from "Release/SignalR/SignalRHubProxies";

import { SignalRSlowMessage } from "Release/Landing/Resources";

export interface IReleasesSignalRHandler {
    onReleaseEnvironmentStatus(
        definitionId: number,
        releaseId: number,
        environmentId: number,
        environmentStatus: EnvironmentStatus,
        deploymentStatus: DeploymentStatus,
        operationStatus: DeploymentOperationStatus): void;

    onReleaseCreated(definitionId: number, releaseId: number): void;

    onDeployJobStartedEvent(definitionId: number, releaseId: number, definitionEnvironmentId: number, releaseEnvironmentId: number): void;

    onReleaseApprovalPending(definitionId: number, releaseId: number, releaseEnvironmentId: number, approvalId: number, approverId: string): void;
}

export interface IReleasesSignalRHubOptions {
    hostInformation: IHubHostInformation;
    projectId: string;
    pageContext: IVssPageContext;
    onDisconnected: () => void;
    onReconnected: () => void;
    onUserInactive: () => void;
}

const s_userInactivityTimeoutMs = 1 * 60 * 60 * 1000; // 1 hour

export class ReleasesSignalRHub extends SignalRHub<IReleaseDetailHubClient, IReleaseDetailHubServer> {

    constructor(options: IReleasesSignalRHubOptions) {
        super({
            hostInformation: options.hostInformation,
            name: HubNames.releaseHub,
            userInactivityTimeoutMs: s_userInactivityTimeoutMs,
            listener: new DefaultSignalRListener(options.onDisconnected, options.onReconnected, options.onUserInactive)
        });

        this._pageContext = options.pageContext;
        this.initializeProxy(getReleaseProxyToRegister, this._initializeClient);
    }

    public watchReleaseDefinitionReleases(projectId: string, definitionId: number) {
        if (this._watchedPipelineIds.has(definitionId)) {
            return;
        }

        this.start().then(() => {
            _signalRHubConnected = true;

            const hub = this.hub;
            if (hub) {
                this._watchedPipelineIds.add(definitionId);
                this._tryExecuteServerWatch(() => {
                    hub.server.watchReleaseDefinitionReleases(projectId, definitionId).then(() => {
                        console.log("ReleasesSignalRHub: watching release pipelines " + definitionId);
                    });
                });
            }
        }).catch((err) => {
            console.log(err);
        });
    }

    public stopWatchingReleaseDefinitionReleases(projectId: string, definitionId: number) {
        this._watchedPipelineIds.delete(definitionId);

        const hub = this.hub;
        if (hub) {
            this._tryExecuteServerWatch(() => {
                hub.server.stopWatchingReleaseDefinitionReleases(projectId, definitionId);
                console.log("ReleasesSignalRHub: Stopped watching release pipeline " + definitionId);
            });
        }
    }

    public watchReleaseDefinitionsReleases(projectId: string, definitionIds: number[]) {
        const filteredDefinitionIds = definitionIds.filter(defId => !this._watchedPipelineIds.has(defId));

        if (filteredDefinitionIds.length) {
            this.start().then(() => {
                _signalRHubConnected = true;

                const hub = this.hub;
                if (hub) {
                    definitionIds.forEach(defId => this._watchedPipelineIds.add(defId));

                    this._tryExecuteServerWatch(() => {
                        hub.server.watchReleaseDefinitionsReleases(projectId, filteredDefinitionIds).then(() => {
                            console.log("ReleasesSignalRHub: watching release pipelines " + filteredDefinitionIds.join(","));
                        });
                    });
                }
            }).catch((err) => {
                console.log(err);
            });
        }
    }

    public stopWatchingReleaseDefinitionsReleases(projectId: string, definitionIds: number[]) {
        definitionIds.forEach(defId => this._watchedPipelineIds.delete(defId));

        const hub = this.hub;
        if (hub) {
            this._tryExecuteServerWatch(() => {
                hub.server.stopWatchingReleaseDefinitionsReleases(projectId, definitionIds);
                console.log("ReleasesSignalRHub: Stopped watching release pipeline " + definitionIds.join(","));
            });
        }
    }

    public stopWatchingAllPipelines(projectId: string) {
        const definitionIds = Array.from(this._watchedPipelineIds);
        this._watchedPipelineIds.clear();

        const hub = this.hub;
        if (hub) {
            this._tryExecuteServerWatch(() => {
                hub.server.stopWatchingReleaseDefinitionsReleases(projectId, definitionIds);
                console.log("ReleasesSignalRHub: Stopped watching release pipeline " + definitionIds.join(","));
            });
        }
    }

    public addSignalRHanlder(handler: IReleasesSignalRHandler): void {
        this._handlers.push(handler);
    }

    public removeSignalRHanlder(handler: IReleasesSignalRHandler): void {
        const index = this._handlers.findIndex(h => h === handler);
        if (index >= 0) {
            this._handlers.splice(index, 1);
        }
    }

    private _tryExecuteServerWatch(action: () => void): boolean {
        try {
            action();
            return true;
        }
        catch (ex) {
            console.log(ex);
            traceHubException(this._pageContext, FeatureArea, ex);
            return false;
        }
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IReleaseDetailHubClient, IReleaseDetailHubServer>) => {
        proxy.client.onReleaseEnvironmentStatusUpdated = (projectId: string, definitionId: number, releaseId: number, environmentId: number, environmentStatus: EnvironmentStatus, deploymentStatus: DeploymentStatus, deploymentOperationStatus: DeploymentOperationStatus) => {

            // The signalR object doesn't serialize enums to numbers, but leave them as strings
            const convertedEnvironmentStatus = ReleaseContractsStatusConverter.toEnvironmentStatus(environmentStatus as any);
            const convertedDeploymentStatus = ReleaseContractsStatusConverter.toDeploymentStatus(deploymentStatus as any);
            const convertedOperationStatus = ReleaseContractsStatusConverter.toDeploymentOperationStatus(deploymentOperationStatus as any);

            this._handlers.forEach(
                h =>
                    h.onReleaseEnvironmentStatus(definitionId, releaseId, environmentId, convertedEnvironmentStatus, convertedDeploymentStatus, convertedOperationStatus));
        };

        proxy.client.onReleaseCreated = (projectId: string, definitionId: number, releaseId: number) => {
            this._handlers.forEach(
                h =>
                    h.onReleaseCreated(definitionId, releaseId));
        };

        proxy.client.onDeployJobStartedEvent = (projectId: string, definitionId: number, releaseId: number, definitionEnvironmentId: number, releaseEnvironmentId: number) => {
            this._handlers.forEach(
                h =>
                    h.onDeployJobStartedEvent(definitionId, releaseId, definitionEnvironmentId, releaseEnvironmentId)
            );
        }

        proxy.client.onReleaseApprovalPending = (projectId: string, definitionId: number, releaseId: number, releaseEnvironmentId: number, approvalId: number, approverId: string) => {
            this._handlers.forEach(
                h =>
                    h.onReleaseApprovalPending(definitionId, releaseId, releaseEnvironmentId, approvalId, approverId)
            );
        }
    }

    private _handlers: IReleasesSignalRHandler[] = [];
    private _pageContext: IVssPageContext;
    private _watchedPipelineIds: Set<number> = new Set<number>();
}

export class DefaultSignalRListener implements IHubListener {
    constructor(
        private _onDisconnected: () => void,
        private _onReconnected: () => void,
        private _onUserInactive: () => void
    ) {
    }

    public isConnecting(): void {
    }

    public isConnected(): void {
    }

    public isDisconnected(): void {
        this._onDisconnected();
    }

    public isReconnected(): void {
        this._onReconnected();
    }

    public isReconnecting(): void {
    }

    public isSlow(): void {
        console.warn(SignalRSlowMessage);
    }

    public isUserActive(): void {
    }

    public isUserInactive(): void {
        this._onUserInactive();
    }
}


export function getReleasesSignalRHub(): ReleasesSignalRHub | undefined {
    return _signalRHub;
}

export function disconnectSignalRHub(): void {
    if (_signalRHub) {
        _signalRHub.stop();
    }

    _signalRHubConnected = false;
}

export function createSignalRHubIfNecessary(options: IReleasesSignalRHubOptions): void {
    if (!_signalRHub) {
        _signalRHub = new ReleasesSignalRHub(options);
    }
}

export function isSignalRConnected(): boolean {
    return _signalRHubConnected;
}


let _signalRHub: ReleasesSignalRHub | undefined;
let _signalRHubConnected: boolean = false;